import React, { useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Key as KeyIcon,
    Pin as PinIcon,
    Devices as DevicesIcon,
    Home as HomeIcon,
    Logout as LogoutIcon,
    Feedback as FeedbackIcon,
    FamilyRestroom as FamilyRestroomIcon,
    Cake as CakeIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLogoutMutation, useSetDatumNarozeniMutation } from '../../api/JsdhNotifikaceService/service/AuthMutation';
import { useDemoStatusQuery } from '../../api/JsdhNotifikaceService/service/FeedbackMutations';
import ChangePasswordDialog from '../ChangePasswordDialog';
import SetPinDialog from '../SetPinDialog';
import FeedbackDialog from './FeedbackDialog';
import RodinnyPristupDialog from '../RodinnyPristupDialog';

// ==========================================
// DIALOG: Doplnit datum narození (self-service)
// ==========================================

const DatumNarozeniDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const [value, setValue] = useState<Dayjs | null>(null);
    const mutation = useSetDatumNarozeniMutation();

    const handleSave = () => {
        if (!value) return;
        mutation.mutate(value.format('YYYY-MM-DD'), { onSuccess: onClose });
    };

    const handleClose = () => {
        setValue(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Doplnit datum narození</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Datum narození slouží k šifrování zasílaného lékařského posudku.
                </Typography>
                <DatePicker
                    label="Datum narození"
                    value={value}
                    onChange={setValue}
                    format="DD.MM.YYYY"
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={mutation.isPending}>Zrušit</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!value || mutation.isPending}
                    startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
                >
                    Uložit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ==========================================
// MAIN: User Dropdown Menu
// ==========================================

const UserDropdownMenu: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();
    const logoutMutation = useLogoutMutation();
    const { data: demoStatus } = useDemoStatusQuery();
    const demoEnabled = demoStatus?.demoEnabled === true;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [setPinOpen, setSetPinOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [rodinnyPristupOpen, setRodinnyPristupOpen] = useState(false);
    const [datumNarozeniOpen, setDatumNarozeniOpen] = useState(false);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleClose();
        logoutMutation.mutate(undefined, {
            onSuccess: () => {
                navigate('/login');
            },
        });
    };

    const initials = user?.jmeno
        ? user.jmeno
              .split(' ')
              .filter(Boolean)
              .map((n) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()
        : '?';

    return (
        <>
            <Tooltip title="Uživatelský účet">
                <IconButton
                    onClick={handleClick}
                    size="small"
                    aria-controls={open ? 'user-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Avatar
                        sx={{
                            width: 36,
                            height: 36,
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            color: theme.palette.primary.main,
                        }}
                    >
                        {initials}
                    </Avatar>
                </IconButton>
            </Tooltip>

            <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            minWidth: 220,
                            borderRadius: 3,
                            overflow: 'visible',
                        },
                    },
                }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        {user?.jmeno || 'Uživatel'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {user?.email || ''}
                    </Typography>
                </Box>
                <Divider />
                <MenuItem
                    onClick={() => {
                        handleClose();
                        navigate('/admin/sessions');
                    }}
                >
                    <ListItemIcon>
                        <DevicesIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Aktivní sessions</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        setChangePasswordOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <KeyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Změnit heslo</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        setSetPinOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <PinIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Nastavit PIN</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        setRodinnyPristupOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <FamilyRestroomIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Rodinný přístup</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        setDatumNarozeniOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <CakeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Doplnit datum narození</ListItemText>
                </MenuItem>
                {!demoEnabled && (
                    <MenuItem
                        onClick={() => {
                            handleClose();
                            setFeedbackOpen(true);
                        }}
                    >
                        <ListItemIcon>
                            <FeedbackIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Zpětná vazba</ListItemText>
                    </MenuItem>
                )}
                <Divider />
                <MenuItem
                    onClick={() => {
                        handleClose();
                        navigate('/');
                    }}
                >
                    <ListItemIcon>
                        <HomeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Hlavní stránka</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Odhlásit se</ListItemText>
                </MenuItem>
            </Menu>

            <ChangePasswordDialog
                open={changePasswordOpen}
                onClose={() => setChangePasswordOpen(false)}
            />
            <SetPinDialog
                open={setPinOpen}
                onClose={() => setSetPinOpen(false)}
            />
            <FeedbackDialog
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
            />
            <RodinnyPristupDialog
                open={rodinnyPristupOpen}
                onClose={() => setRodinnyPristupOpen(false)}
            />
            <DatumNarozeniDialog
                open={datumNarozeniOpen}
                onClose={() => setDatumNarozeniOpen(false)}
            />
        </>
    );
};

export default UserDropdownMenu;
